import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useAuth } from '@/auth/context'
import { relativeTime } from '@/lib/format'
import { useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Danger zone — S-D3's last settings panel, on S-B12's endpoints.
 *
 * S-B8 left this one out and said why: DPDP export and organisation deletion
 * are launch requirements S-B12 owns, and "pause all automation" has no
 * automation to pause. Two of those three are now built; the third is still
 * true, so it is rendered the way the sidebar renders an unshipped module —
 * present, disabled, and naming the session it arrives in — rather than as a
 * button that does nothing.
 *
 * **The export is asynchronous and the screen says so.** S-D3's own copy is
 * "emailed within 24 hours", and the API answers `202`: building the archive
 * streams every table in the organisation's schema. What this panel shows is
 * the queue and the history, so "did my request go through" has an answer that
 * is not an email that has not arrived yet.
 *
 * **The delete confirmation is checked on the server, not here.** Typing the
 * name is what the modal asks for and the button stays disabled until it
 * matches — but the API refuses a mismatch with `CONFIRMATION_REQUIRED`
 * regardless, because a confirmation enforced only in the browser is a
 * confirmation an accidental `curl` skips. The two are not redundant: one
 * prevents the accident, the other prevents the mistake.
 */
export function DangerZonePanel(): JSX.Element {
  const { tenant } = useActiveTenant()
  const { signOut } = useAuth()
  const toast = useToast()
  const [confirmation, setConfirmation] = useState('')
  const [deleting, setDeleting] = useState(false)
  const now = new Date()

  const exports = useQuery((signal) => call(() => api.GET('/api/v1/exports', { signal })), [])
  const retention = useQuery((signal) => call(() => api.GET('/api/v1/retention', { signal })), [])

  const request = useMutation(() => call(() => api.POST('/api/v1/exports', {})))
  const download = useMutation((exportId: number) =>
    call(() =>
      api.POST('/api/v1/exports/{export_id}/download', {
        params: { path: { export_id: exportId } },
      }),
    ),
  )
  const erase = useMutation((typed: string) =>
    call(() =>
      api.POST('/api/v1/organisation/deletion', { body: { confirmation: typed, reason: null } }),
    ),
  )

  const rows = exports.state.data?.exports ?? []
  const inFlight = rows.some((row) => row.state === 'pending' || row.state === 'running')
  const keptDays = retention.state.data?.export_retained_days ?? null

  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Data &amp; automation</h3>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="app-danger__row">
            <div>
              <strong>Pause all automation</strong>
              <p className="ar-muted app-danger__note">
                Stops audits, content, and outreach. Your data and rankings stay. Reversible.
              </p>
            </div>
            {/* Disabled rather than hidden, exactly as the sidebar treats a
                module that has not shipped: there is no automation to pause
                until the SEO and outreach runners land. */}
            <Button disabled title="Arrives with the automation it pauses (S-B16)">
              Pause
            </Button>
          </div>

          <hr className="app-danger__rule" />

          <div className="app-danger__row">
            <div>
              <strong>Export everything (DPDP)</strong>
              <p className="ar-muted app-danger__note">
                A machine-readable copy of every record we hold about your organisation and its
                leads. You will be emailed when it is ready
                {keptDays === null
                  ? ''
                  : `, and it stays downloadable for ${String(keptDays)} days`}
                .
              </p>
            </div>
            <Button
              busy={request.pending}
              busyLabel="Requesting"
              disabled={inFlight}
              onClick={() => {
                void (async () => {
                  const result = await request.run(undefined)
                  if (!result.ok) return
                  exports.refetch()
                  toast.show({
                    kind: 'success',
                    title: 'Export requested',
                    body: 'We will email you when the archive is ready.',
                  })
                })()
              }}
            >
              {inFlight ? 'Being prepared' : 'Request export'}
            </Button>
          </div>
        </div>

        <InlineError failure={request.state.failure ?? download.state.failure} />

        {exports.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading your exports" lines={2} />
          </div>
        ) : null}

        {exports.state.status === 'error' ? (
          <ErrorState
            failure={exports.state.failure}
            title="Could not load your exports"
            onRetry={exports.refetch}
          />
        ) : null}

        {exports.state.status === 'ready' && rows.length === 0 ? (
          <Empty
            title="No exports yet"
            body="Ask for one above. It is built in the background and emailed to you."
          />
        ) : null}

        {rows.length > 0 ? (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>State</th>
                  <th>Size</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="ar-muted">{relativeTime(row.created_at, now)}</td>
                    <td>
                      <span className={`ar-badge ar-badge--${badgeTone(row.state)}`}>
                        {stateLabel(row.state)}
                      </span>
                      {row.error === null || row.error === undefined ? null : (
                        <div className="ar-subtle app-danger__caption">{row.error}</div>
                      )}
                    </td>
                    <td className="ar-muted ar-num">{formatBytes(row.byte_size ?? null)}</td>
                    <td className="ar-num">
                      {row.downloadable ? (
                        <Button
                          tone="quiet"
                          size="sm"
                          disabled={download.pending}
                          onClick={() => {
                            void (async () => {
                              const result = await download.run(row.id)
                              if (!result.ok) return
                              // A short-lived signed URL, minted on this press
                              // and never stored — so it is followed at once
                              // rather than rendered as a link somebody might
                              // come back to after it has expired.
                              window.location.assign(result.data.url)
                            })()
                          }}
                        >
                          Download
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="ar-card app-danger">
        <div className="ar-card__header">
          <h3 style={{ color: 'var(--ar-danger)' }}>Danger zone</h3>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="app-danger__row">
            <div>
              <strong style={{ color: 'var(--ar-danger)' }}>Delete this organisation</strong>
              <p className="ar-muted app-danger__note">
                Removes your tenant schema, all content, leads, and history. Published pages stay on
                your own site. <strong>This cannot be undone.</strong> We keep billing records for 8
                years as Indian law requires.
              </p>
            </div>
            <Button
              tone="danger"
              onClick={() => {
                setConfirmation('')
                setDeleting(true)
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </section>

      {deleting ? (
        <Modal
          title={`Delete ${tenant.name}?`}
          onClose={() => {
            setDeleting(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setDeleting(false)
                }}
              >
                Keep my account
              </Button>
              <Button
                tone="danger"
                busy={erase.pending}
                busyLabel="Deleting"
                disabled={confirmation.trim() !== tenant.name}
                onClick={() => {
                  void (async () => {
                    const result = await erase.run(confirmation.trim())
                    if (!result.ok) return
                    setDeleting(false)
                    // Signed out rather than routed onward: the session was
                    // scoped to an organisation that no longer exists, and the
                    // server revoked it as the first step of the erasure.
                    await signOut()
                  })()
                }}
              >
                Delete permanently
              </Button>
            </>
          }
        >
          <InlineError failure={erase.state.failure} />
          <p style={{ marginTop: 0 }}>
            This deletes your tenant schema and everything in it — every keyword, article, lead and
            reasoning trail. Anybody else in {tenant.name} loses access immediately.
          </p>
          <p className="ar-muted">
            If you want a copy first, close this and request an export. It cannot be produced after
            the organisation is gone.
          </p>
          <Field label={`Type ${tenant.name} to confirm`}>
            {(props) => (
              <input
                {...props}
                className="ar-input"
                autoComplete="off"
                value={confirmation}
                placeholder={tenant.name}
                onChange={(event) => {
                  setConfirmation(event.target.value)
                }}
              />
            )}
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

function stateLabel(state: string): string {
  // "expired" and "failed" are different answers and the screen says which: one
  // means the archive aged out, the other that it was never built.
  const labels: Record<string, string> = {
    pending: 'Queued',
    running: 'Building',
    ready: 'Ready',
    failed: 'Failed',
    expired: 'Expired',
  }
  return labels[state] ?? state
}

function badgeTone(state: string): string {
  if (state === 'ready') return 'success'
  if (state === 'failed') return 'danger'
  if (state === 'expired') return 'neutral'
  return 'info'
}

function formatBytes(size: number | null): string {
  if (size === null) return '—'
  if (size < 1000) return `${String(size)} B`
  if (size < 1_000_000) return `${(size / 1000).toFixed(1)} KB`
  return `${(size / 1_000_000).toFixed(1)} MB`
}
